import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, styled } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography, { TypographyProps } from '@mui/material/Typography';

import previewImg from 'assets/indicators_preview/horizontal_bar.png';
import { LibraryIndicatorT } from 'business/definitions';
import { useIsExploreUser } from 'hooks/client';
import SeeMoreButton from 'ui/AdaptiveChipList/SeeMoreButton';
import IndicatorActions from './IndicatorActions';
import IndicatorTag from './ui/IndicatorTag';
import SeeOriginalButton from './ui/SeeOriginalButton';
import { INDICATOR_CARD_WIDTH } from '../helpers/constants';
import { getIndicatorTags } from '../helpers/helpers';
import { IndicatorTagT, isCityIndicator } from '../helpers/types';

const StyIndicatorCard = styled(Paper)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    justifyContent: 'space-between',
    height: '400px',
    padding: theme.spacing(2),
    width: `${INDICATOR_CARD_WIDTH}px`,
}));

const StyPreviewImage = styled('img', {
    shouldForwardProp: (prop) => prop !== 'areFilterChipsOverflowing',
})<{
    areFilterChipsOverflowing: boolean;
}>(({ areFilterChipsOverflowing }) => ({
    objectFit: 'contain',
    height: areFilterChipsOverflowing ? 170 : 205,
}));

const StyIndicatorHeaderContainer = styled('div', {
    shouldForwardProp: (prop) => prop !== 'showSeeOriginalButton',
})<{ showSeeOriginalButton: boolean }>(({ showSeeOriginalButton }) => ({
    alignItems: 'center',
    display: 'flex',
    ...(showSeeOriginalButton
        ? {
              justifyContent: 'space-between',
          }
        : {}),
}));

const IndicatorContentWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const StyIndicatorTitle = styled(Typography, {
    shouldForwardProp: (prop) => prop !== 'showSeeOriginalButton',
})<TypographyProps & { showSeeOriginalButton: boolean }>(
    ({ showSeeOriginalButton, theme }) => ({
        '&:first-letter': {
            textTransform: 'initial', // needed to remove empty space at the end of the text
        },
        ...(showSeeOriginalButton ? { flex: 1 } : {}),
        marginRight: theme.spacing(1),
    }),
);

// This value corresponds to the height of two chips + a gap of 8px between them
const INDICATOR_TAG_SMALL_CONTAINER_MAX_HEIGHT = '56px';
// This value corresponds to the height of 8 chips + a gap of 8px between them
const INDICATOR_TAG_LARGE_CONTAINER_MAX_HEIGHT = '248px';

const StyIndicatorTagsContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'showMoreFilters',
})<{ showMoreFilters: boolean }>(({ showMoreFilters, theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexFlow: 'row wrap',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    maxHeight: showMoreFilters
        ? INDICATOR_TAG_LARGE_CONTAINER_MAX_HEIGHT
        : INDICATOR_TAG_SMALL_CONTAINER_MAX_HEIGHT,
    overflowY: showMoreFilters ? 'auto' : 'hidden',
    width: '100%',
}));

const IndicatorChipsList = (indicatorTags: IndicatorTagT[]) =>
    indicatorTags.map((indicatorTag) => (
        <IndicatorTag
            key={`filter-chip-${indicatorTag.value}`}
            indicatorTag={indicatorTag}
        />
    ));

type IndicatorCardProps = {
    indicator: LibraryIndicatorT;
    isModel: boolean;
};

const IndicatorCard = ({
    indicator,
    isModel,
}: IndicatorCardProps): JSX.Element => {
    const { t } = useTranslation();

    const chipsContainerRef = useRef<HTMLDivElement>(null);
    const [showMoreChips, setShowMoreChips] = useState(false);
    const [areFilterChipsOverflowing, setAreFilterChipsOverflowing] =
        useState(false);

    const isExploreUser = useIsExploreUser();

    useEffect(() => {
        const chipsContainer = chipsContainerRef.current;
        if (chipsContainer) {
            setAreFilterChipsOverflowing(
                chipsContainer.scrollHeight > chipsContainer.clientHeight,
            );
        }
    }, []);

    const indicatorTags = getIndicatorTags(indicator, t);
    const showSeeOriginalButton = isCityIndicator(indicator);
    return (
        <StyIndicatorCard>
            <IndicatorContentWrapper>
                <StyIndicatorHeaderContainer
                    showSeeOriginalButton={showSeeOriginalButton}
                >
                    <Tooltip title={indicator.title} enterDelay={1000}>
                        <StyIndicatorTitle
                            noWrap
                            showSeeOriginalButton={showSeeOriginalButton}
                            variant="body1SemiBold"
                        >
                            {indicator.title}
                        </StyIndicatorTitle>
                    </Tooltip>
                    {showSeeOriginalButton ? (
                        <SeeOriginalButton
                            route={
                                isExploreUser
                                    ? `/explore/${indicator.key}`
                                    : `/dashboard/${indicator.dashboardSlug}#widget-${indicator.widgetKey}`
                            }
                        />
                    ) : (
                        !!indicator.description && (
                            <Tooltip title={indicator.description}>
                                <InfoIcon fontSize="small" />
                            </Tooltip>
                        )
                    )}
                </StyIndicatorHeaderContainer>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <StyIndicatorTagsContainer
                        showMoreFilters={showMoreChips}
                        ref={chipsContainerRef}
                    >
                        {IndicatorChipsList(indicatorTags)}
                    </StyIndicatorTagsContainer>
                    {areFilterChipsOverflowing && (
                        <SeeMoreButton
                            showMore={showMoreChips}
                            onClick={() => setShowMoreChips(!showMoreChips)}
                        />
                    )}
                </div>
            </IndicatorContentWrapper>
            {!showMoreChips && (
                <StyPreviewImage
                    alt="Indicator preview image"
                    areFilterChipsOverflowing={areFilterChipsOverflowing}
                    src={
                        isCityIndicator(indicator)
                            ? previewImg
                            : indicator.preview
                    }
                />
            )}
            <IndicatorActions
                indicator={indicator}
                isExploreUser={isExploreUser}
                isModel={isModel}
            />
        </StyIndicatorCard>
    );
};

export default IndicatorCard;
